import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import zh from './locales/zh.json';
import hi from './locales/hi.json';
import es from './locales/es.json';
import ar from './locales/ar.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';
import ru from './locales/ru.json';
import de from './locales/de.json';
import ja from './locales/ja.json';
import tr from './locales/tr.json';
import uz from './locales/uz.json';

const FALLBACK_CODE = 'en';

// Uzbek first as the author's own language, then roughly by number of speakers worldwide.
export const languages = [
  { code: 'uz', nativeName: "O'zbekcha", flag: '🇺🇿' },
  { code: 'en', nativeName: 'English', flag: '🇬🇧' },
  { code: 'zh', nativeName: '中文', flag: '🇨🇳' },
  { code: 'hi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'es', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'fr', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'ru', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'de', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'tr', nativeName: 'Türkçe', flag: '🇹🇷' },
];

// Falls back to English rather than the first entry, to stay in step with
// i18next's fallbackLng regardless of how the list above is ordered.
export const getLanguage = (code) =>
  languages.find((language) => language.code === code) ||
  languages.find((language) => language.code === FALLBACK_CODE);

// Accepts regional codes too, so both "ar" and "ar-EG" resolve to rtl.
export const getDirection = (code) => getLanguage(String(code).split('-')[0]).dir || 'ltr';

const resources = { en, zh, hi, es, ar, fr, pt, ru, de, ja, tr, uz };

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: Object.fromEntries(
      Object.entries(resources).map(([code, translation]) => [code, { translation }])
    ),
    fallbackLng: 'en',
    supportedLngs: languages.map((language) => language.code),
    // Map regional codes onto their base language, so en-US resolves to en and zh-CN to zh.
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'language',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Keep <html lang> and <html dir> in sync so screen readers, search engines
// and RTL layout follow the selected language.
const applyDocumentLanguage = (code) => {
  document.documentElement.lang = getLanguage(String(code).split('-')[0]).code;
  document.documentElement.dir = getDirection(code);
};

applyDocumentLanguage(i18n.resolvedLanguage || i18n.language || 'en');
i18n.on('languageChanged', applyDocumentLanguage);

export default i18n;
